import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, EmptyState } from "../components/shared";
import { can } from "../lib/constants";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Plus, Pencil, Trash2, Warehouse as WhIcon, Star } from "lucide-react";
import { toast } from "sonner";

export default function Warehouses() {
  const { user } = useAuth();
  const editable = can(user.role, "warehouses");
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", location: "" });
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => setItems((await api.get("/warehouses")).data), []);
  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing) await api.put(`/warehouses/${editing}`, form);
      else await api.post("/warehouses", form);
      toast.success("Saved");
      setOpen(false);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete warehouse?")) return;
    try { await api.delete(`/warehouses/${id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <div data-testid="warehouses-page">
      <PageHeader title="Warehouses" subtitle="Manage storage locations for your organization"
        action={editable && (
          <Button className="rounded-none gap-2" data-testid="create-warehouse-btn"
            onClick={() => { setForm({ name: "", location: "" }); setEditing(null); setOpen(true); }}>
            <Plus className="w-4 h-4" /> New Warehouse
          </Button>
        )} />

      {items.length === 0 ? <EmptyState title="No warehouses" /> : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((w) => (
            <div key={w.id} className="border border-border bg-card p-5" data-testid="warehouse-card">
              <div className="flex items-start justify-between">
                <div className="w-10 h-10 bg-secondary flex items-center justify-center"><WhIcon className="w-5 h-5" /></div>
                {w.is_default && <span className="flex items-center gap-1 text-xs font-mono text-primary-foreground bg-primary px-2 py-0.5"><Star className="w-3 h-3" />DEFAULT</span>}
              </div>
              <h3 className="font-display text-lg font-bold mt-3">{w.name}</h3>
              <p className="text-sm text-muted-foreground">{w.location || "No location set"}</p>
              {editable && (
                <div className="flex gap-1 mt-4">
                  <Button size="sm" variant="outline" className="rounded-none gap-1" onClick={() => { setForm(w); setEditing(w.id); setOpen(true); }}>
                    <Pencil className="w-3.5 h-3.5" /> Edit
                  </Button>
                  {!w.is_default && (
                    <Button size="sm" variant="ghost" className="rounded-none text-destructive gap-1" onClick={() => del(w.id)}>
                      <Trash2 className="w-3.5 h-3.5" /> Delete
                    </Button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none">
          <DialogHeader><DialogTitle className="font-display">{editing ? "Edit" : "New"} Warehouse</DialogTitle><DialogDescription>Add or edit a storage location.</DialogDescription></DialogHeader>
          <form onSubmit={save} className="space-y-3">
            <div><Label>Name</Label><Input className="rounded-none mt-1" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} required data-testid="warehouse-name-input" /></div>
            <div><Label>Location</Label><Input className="rounded-none mt-1" value={form.location} onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))} /></div>
            <DialogFooter><Button type="submit" className="rounded-none" data-testid="save-warehouse-btn">Save</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
